import environment from '../../../common/environment';
import { SearchResultDocument } from '../interfaces/search.interface';
import { getImageSrcUrl, getPdpLink } from '../../../common/utils/product-util';
import { getProfessionalPdpLink } from '../../../common/constants/professional-redirects';
import { html, nothing } from 'lit';
import { isSearchPage } from '../../../common/utils/util';
import { TAB_NAMES } from '../../tabbed-search/constants';
import "../../tabbed-search/tabbed-search-result/tabbed-suggestion-search-result";
import { searchParam } from '../store/search.selector';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';

/**
 * Retrieves the image for the search results.
 *
 * @param document The document to get the search product data
 * @returns image if available or create using keys image/id/isbn/isbn13
 */
export function getImage(document: SearchResultDocument) {
  if (document?.imagePath) {
    return environment().mheducationUrl + document.imagePath;
  } else {
    return getImageSrcUrl(document?.image || document?.id || document.isbn || document.isbn13);
  }
}

/**
 * This will generate of url to send user to a specific page and identify the pragram/product belongs to which catalog
 *
 * @param document The document to get the search product data
 * @param catalog
 * @param store
 * @param showUrlsAsSearchPage
 * @returns object with url and catalogLabel
 *
 * TODO: refactor: remove showUrlsAsSearchPage check
 */
export function getProductLabelAndUrl(
  document: SearchResultDocument,
  catalog: string,
  store?: string,
  showUrlsAsSearchPage = false
) {
  let catalogLabel = '';
  let kind = 'ContentCardData';
  let businessUnit = catalog;
  let url = document.path;
  let isHEDocument = false;
  let isSegDocument = false;
  let isProfessional = false;

  if (showUrlsAsSearchPage) {
    switch (document.type) {
      case 'HEDocument':
        if (document.businessUnit === 'Professional') {
          isProfessional = true;
        } else {
          isHEDocument = true;
        }
        break;
      case 'SEGProgramDocument':
        if (document.documentType == 'Programs') {
          catalogLabel = 'PreK-12 Program';
        } else {
          catalogLabel = 'PreK-12 Product';
          kind = 'ProductCardData';
          businessUnit = 'seg-product';
        }
        isSegDocument = true;
        break;
      case 'SEGProductDocument':
        catalogLabel = 'PreK-12 Product';
        kind = 'ProductCardData';
        businessUnit = 'seg-product';
        isSegDocument = true;
        break;
      case 'ContentDocument':
        url = document.path;
        break;
      case 'UnifiedDocument':
        if ((document.documentType == 'HigherEd') || (document.documentType == 'Programs' && document?.businessUnit == 'Professional')) {
          catalogLabel = 'Higher Ed Product';
          isHEDocument = true;
        } else if (document.documentType == 'Programs'  && document.businessUnit == 'HigherEd') {
          catalogLabel = 'Higher Ed Product';
          isHEDocument = true;
        } else if (document.documentType == 'Programs') {
          catalogLabel = 'PreK-12 Program';
          isSegDocument = true;
        } else if (document.documentType == 'Products') {
          catalogLabel = 'PreK-12 Product';
          kind = 'ProductCardData';
          businessUnit = 'seg-product';
          isSegDocument = true;
        } else if (document.documentType == 'content') {
          catalogLabel = 'Topics & Ideas';
          url = document.path;
        }
        break;
    }

    if (isHEDocument) {
      if (document?.businessUnit == 'Professional') {
        url = getProfessionalPdpLink(document.mhid, document.seoTitle);
      } else if (document.optimizedUniqueTitle) {
        url = '/highered/product/' + document.optimizedUniqueTitle.toLowerCase() + '.html';
      } else if (document.IS_MASTER === true) {
        url = '/highered/product/' + document.seoTitle + '/' + 'M' + document.isbn13 + '.html';
      } else {
        url = '/highered/product/' + (document.seoTitle ? document.seoTitle + '/' : '') + document.isbn13 + '.html';
      }
    }

    if (isSegDocument) {
      if (document.isbn13 && document.isbn13 != 'N/A') {
        url = '/prek-12/product/' + document.seoTitle + '/' + document.isbn13 + '.html';
      } else if (document.mhid) {
        url = '/prek-12/product/' + document.seoTitle + '/' + document.mhid + '.html';
      } else if (document.isbn) {
        url = '/prek-12/product/' + document.seoTitle + '/' + document.isbn + '.html';
      } else if (document.id) {
        url = '/prek-12/program/' + document.seoTitle + '/' + document.id + '.html';
      }
    }

    if (isProfessional) {
      url = getProfessionalPdpLink(document.mhid ?? document.isbn, document.seoTitle);
    }

    url = environment().mheducationUrl + url;
  } else {
    url = getPdpLink({
      businessUnit: catalog,
      isbn: document.isbn13 === 'N/A' ? '' : document.isbn13,
      store: store,
      seoTitle: document.seoTitle,
      mhid: document.mhid || document.isbn,
    });
  }
  return {catalogLabel: catalogLabel, kind: kind, businessUnit: businessUnit, url: url};
}

/**
 * Checks if the product from the search results is published or not.
 *
 * @param publicationDateString
 * @returns boolean
 */
export function isPublished(publicationDateString: string): boolean | undefined {
  return publicationDateString ? new Date(publicationDateString) > new Date() : false;
}

/**
 * Renders the custom ISBN template.
 */
export function renderCustomISBNTemplate() {
  return html`
    <div id="custom-isbn-search-message" class="p-1">
      <span class="fw-semibold">The ISBN you searched for is a discounted product.</span>
      <ul class="mt-2">
        <li>
          Please use the registration URL provided by instructor in order to
          <span class="fw-semibold">receive the discount.</span>
        </li>
        <li><a id="continue-shopping" href="${environment().mheducationUrl}">Continue Shopping</a></li>
      </ul>
    </div>
  `;
}

/**
 * Renders the custom ISBN or no result template.
 */
export function renderNoResultTemplate(tabName?: string) {
  return html`
    <section id="category-search-tips">
      <h1 class="fw-light">Search Tips</h1>
      ${tabName ? html`
          <div class="mt-4">
            <mhecomm-tabbed-suggestion-search-result
              searchterm="${searchParam(store.getState() as StoreState) || ''}"
            ></mhecomm-tabbed-suggestion-search-result>
          </div>
        ` : nothing}
      <section>
        <h2 class="fw-light">Product Search</h2>
        <ul class="fw-semibold">
          <li>Search by ISBN or MHID for best results</li>
          <li>Product title searches should not include edition or copyright year</li>
          <li>Search for Higher Ed products using access code</li>
        </ul>
      </section>
      <section>
        <h3 class="fw-light">Content Search</h3>
        <ul class="fw-semibold">
          <li>Enter the title of press release or article, excluding the publish date</li>
          <li>Enter popular topics or trends (e.g., STEM, commons core, adaptive learning)</li>
        </ul>
      </section>
      <section>
        <h3 class="fw-light">General</h3>
        <ul class="fw-semibold">
          <li>Make sure search terms are not too specific</li>
          <li>Check your spelling</li>
          <li>Use only author's last name for author searches</li>
          <li>Do not use special characters</li>
        </ul>
      </section>
    </section>
    <article>
      <h1 class="fw-light">Registering an Access Code</h1>
      <span class="fw-semibold">
                  Find your 20-digit access code on a card that came with your text book or was purchased separately
                  (Sample access code: EX22-WT51-07TX-MK33-4KE7).
                </span>
    </article>
  `;
}

export function renderTabbedSearchNoResultTemplate(tabName: string, tabValue: string,) {
  if (tabName === TAB_NAMES.PRODUCT) {
    return renderNoResultTemplate(tabName);
  } else {
    return html`
      <div id="custom-isbn-search-message" class="mt-4">
        <h1 class="fw-semibold h3">No Result Found in ${tabValue}</h1>
        <div><p class="fs-large">Check your spelling or try another search with different search terms.</p></div>
        ${tabName ? html`
          <div class="mt-4">
            <mhecomm-tabbed-suggestion-search-result
              searchterm="${searchParam(store.getState() as StoreState) || ''}"
            ></mhecomm-tabbed-suggestion-search-result>
          </div>
        ` : nothing}
      </div>
      </div>
    `;
  }
}
